import React, { useState, useEffect } from 'react'
import { 
    Segment, 
    Table, 
    Button, 
    Form, 
    Modal, 
    Header,
    Icon,
    Message,
    Accordion,
    Menu as UIMenu,
    Dropdown,
    Radio
} from 'semantic-ui-react'
import { useHistory } from 'react-router-dom'
import Menu from '../FormsComponent/MenuComponent'
import {
    getUsers,
    createUser,
    deleteUser,
    getUserStudies,
    getAllStudies,
    getSubmissions,
    createStudy,
    updateStudy,
    deleteStudy,
    getCustomTranslations,
    createCustomLanguage,
    uploadLanguageVersion,
    deleteCustomLanguage,
    updateUserLanguage,
    updateUserActiveStudy
} from './AdminApi'
import appTranslations, { userTranslations, adminTranslations } from '../../translations'
import { validateTranslationKeys } from '../../utils/validateTranslations'
import { fullMarks, revertResult } from '../addons'
import { getCatalogLanguageOptions, getLanguageDropdownOptions } from '../../assets/languages'
import { LanguageLabel } from '../../assets/FlagIcon'
import SimpleHtmlEditor from './SimpleHtmlEditor'
import { isEmptyHtmlContent, sanitizeHtml } from '../../utils/sanitizeHtml'

const t = appTranslations
const DEFAULT_BREAK_MINUTES = 5

const toBreakDurationMs = (minutes, seconds) => {
    const safeMinutes = Math.max(0, parseInt(minutes, 10) || 0)
    const safeSeconds = Math.max(0, Math.min(59, parseInt(seconds, 10) || 0))
    return (safeMinutes * 60 + safeSeconds) * 1000
}

const fromBreakDurationMs = (breakDurationMs) => {
    const totalSeconds = Math.max(0, Math.floor((breakDurationMs || 0) / 1000))
    return {
        breakMinutes: Math.floor(totalSeconds / 60),
        breakSeconds: totalSeconds % 60
    }
}

const formatBreakDuration = (breakDurationMs) => {
    const totalSeconds = Math.max(0, Math.floor((breakDurationMs || 0) / 1000))
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

const formatDateTime = (value) => {
    if (!value) {
        return '-'
    }
    const locale = localStorage.getItem('ahpLanguage') === 'en' ? 'en-GB' : 'pl-PL'
    return new Date(value).toLocaleString(locale)
}

const getPairwiseValue = (x, y, namesCount, results) => {
    if (!results) {
        return '-'
    }
    if (x === y) {
        return '1'
    }
    let pom = 0
    let count = 0
    if (y > x) {
        while (count < x) {
            count += 1
            pom += namesCount - count
        }
        pom += y - x - 1
        return fullMarks[results[pom]] || '-'
    }
    while (count < y) {
        count += 1
        pom += namesCount - count
    }
    pom += x - y - 1
    const value = fullMarks[results[pom]]
    return revertResult[value] || '-'
}

const escapeCsvValue = (value) => {
    const text = String(value ?? '')
    if (/[",\n\r]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`
    }
    return text
}

const toCsvRow = (cells) => cells.map(escapeCsvValue).join(';')

const buildSubmissionCsv = (submission) => {
    const lines = []
    lines.push(toCsvRow(['userName', 'userEmail', 'studyName', 'submittedAt', 'updatedAt']))
    lines.push(toCsvRow([
        submission?.user?.name || submission?.userName || '',
        submission?.user?.email || submission?.userEmail || '',
        submission?.study?.name || submission?.studyName || '',
        submission?.submittedAt ? new Date(submission.submittedAt).toISOString() : '',
        submission?.updatedAt ? new Date(submission.updatedAt).toISOString() : ''
    ]))
    lines.push('')
    lines.push(toCsvRow(['section', 'markTitle', 'cr', 'rank', 'option', 'weight', 'rowOption', 'columnOption', 'value']))

    ;(submission?.marks || []).forEach((mark, markIndex) => {
        const markTitle = mark.title || `mark_${markIndex + 1}`
        const names = mark.namesList || []
        const cr = typeof mark.cr === 'number' ? mark.cr.toFixed(3) : ''
        const ranking = [...(mark.ranking || [])].sort((a, b) => (b.score || 0) - (a.score || 0))

        if (ranking.length === 0 && names.length === 0) {
            lines.push(toCsvRow(['ranking', markTitle, cr, '', '', '', '', '', '']))
        }

        ranking.forEach((item, rankIndex) => {
            lines.push(toCsvRow([
                'ranking',
                markTitle,
                cr,
                rankIndex + 1,
                item.name || '',
                Number(item.score || 0).toFixed(3),
                '',
                '',
                ''
            ]))
        })

        names.forEach((rowName, rowIndex) => {
            names.forEach((colName, colIndex) => {
                if (rowIndex >= colIndex) {
                    return
                }
                lines.push(toCsvRow([
                    'comparison',
                    markTitle,
                    cr,
                    '',
                    '',
                    '',
                    rowName,
                    colName,
                    getPairwiseValue(rowIndex, colIndex, names.length, mark.results)
                ]))
            })
        })
    })

    return `\uFEFF${lines.join('\n')}`
}

const getSubmissionStudyId = (submission) => (
    typeof submission?.study === 'object' ? submission.study?._id : submission?.study
)

const buildResultsForOptions = (namesList) => {
    const optionsCount = namesList.length
    const comparisonsCount = optionsCount > 1 ? (optionsCount * (optionsCount - 1)) / 2 : 0
    return Array(comparisonsCount).fill(0)
}

const createLocalKey = () => `mark_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

const createEmptyCategory = () => ({
    localKey: createLocalKey(),
    title: '',
    namesList: ['', ''],
    results: [],
    status: false
})

const getDefaultPageText = (pageType) => {
    if (pageType === 'start') {
        return t.forms.startPage || ''
    }
    if (pageType === 'break') {
        return t.forms.middlePage || ''
    }
    if (pageType === 'end') {
        return t.forms.endPage || ''
    }
    return ''
}

const createStartPage = () => ({ type: 'start', text: getDefaultPageText('start') })
const createBreakPage = () => ({ type: 'break', text: getDefaultPageText('break') })
const createEndPage = () => ({ type: 'end', text: getDefaultPageText('end') })
const createCategoriesPage = () => ({ type: 'categories', title: '', categories: [createEmptyCategory()] })

const createEmptyStudyFormData = () => ({
    name: '',
    users: [],
    breakMinutes: DEFAULT_BREAK_MINUTES,
    breakSeconds: 0,
    pages: [createStartPage(), createEndPage()],
    defaultLanguage: 'pl',
    translations: []
})

const getStudyLanguages = (study) => {
    const languages = [study?.defaultLanguage || 'pl']
    ;(study?.translations || []).forEach((item) => {
        if (item.language && !languages.includes(item.language)) {
            languages.push(item.language)
        }
    })
    return languages
}

const alignStudyTranslation = (translation, pages) => {
    const alignedPages = (pages || []).map((page, pageIndex) => ({
        title: translation.pages?.[pageIndex]?.title || '',
        text: translation.pages?.[pageIndex]?.text || ''
    }))

    const alignedMarks = (pages || []).flatMap((page) => (
        page.type === 'categories' ? (page.categories || []) : []
    )).map((category) => {
        const existingMark = (translation.marks || []).find((mark) => mark.localKey === category.localKey) || {}
        return {
            localKey: category.localKey,
            title: existingMark.title || '',
            namesList: (category.namesList || []).map((_, index) => existingMark.namesList?.[index] || '')
        }
    })

    return {
        language: translation.language,
        name: translation.name || '',
        pages: alignedPages,
        marks: alignedMarks
    }
}

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

const deepMerge = (target, source) => {
    if (!isPlainObject(target) || !isPlainObject(source)) {
        return source
    }
    const result = { ...target }
    Object.keys(source).forEach((key) => {
        if (isPlainObject(source[key]) && isPlainObject(result[key])) {
            result[key] = deepMerge(result[key], source[key])
        } else {
            result[key] = source[key]
        }
    })
    return result
}

const AdminPanel = () => {
    const [activeView, setActiveView] = useState('users')
    
    // Users state
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [studiesModalOpen, setStudiesModalOpen] = useState(false)
    const [selectedUser, setSelectedUser] = useState(null)
    const [userStudies, setUserStudies] = useState([])
    const [activeStudyIdForUser, setActiveStudyIdForUser] = useState(null)
    const [activeUserStudyAccordionIndex, setActiveUserStudyAccordionIndex] = useState(-1)
    const [loadingStudies, setLoadingStudies] = useState(false)
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'USER'
    })
    const [submitting, setSubmitting] = useState(false)
    
    // Studies state
    const [studies, setStudies] = useState([])
    const [loadingStudiesList, setLoadingStudiesList] = useState(false)
    const [studyModalOpen, setStudyModalOpen] = useState(false)
    const [studyFormData, setStudyFormData] = useState(createEmptyStudyFormData())
    const [activeStudyPageTab, setActiveStudyPageTab] = useState(0)
    const [studyModalSection, setStudyModalSection] = useState('content')
    const [activeStudyTranslationLanguage, setActiveStudyTranslationLanguage] = useState('')
    const [editingStudy, setEditingStudy] = useState(null)
    const [availableUsers, setAvailableUsers] = useState([])
    const [customUserTranslations, setCustomUserTranslations] = useState({})
    const [customAdminTranslations, setCustomAdminTranslations] = useState({})
    const [languageModalOpen, setLanguageModalOpen] = useState(false)
    const [languageModalScope, setLanguageModalScope] = useState('user')
    const [newLanguageData, setNewLanguageData] = useState({
        language: '',
        translation: null
    })
    const [translationValidation, setTranslationValidation] = useState({
        open: false,
        missing: [],
        extra: []
    })
    const [submissions, setSubmissions] = useState([])
    const [userSubmissions, setUserSubmissions] = useState([])
    const [loadingResponses, setLoadingResponses] = useState(false)
    const [submissionModalOpen, setSubmissionModalOpen] = useState(false)
    const [selectedSubmission, setSelectedSubmission] = useState(null)
    
    const history = useHistory()

    useEffect(() => {
        const role = localStorage.getItem('ahpUserRole')
        if (role !== 'ADMIN') {
            history.push('/forms')
        }
    }, [history])

    useEffect(() => {
        loadCustomTranslations()
    }, [])

    const loadCustomTranslations = async () => {
        const [fetchedUserTranslations, fetchedAdminTranslations] = await Promise.all([
            getCustomTranslations('user'),
            getCustomTranslations('admin')
        ])
        setCustomUserTranslations(fetchedUserTranslations)
        setCustomAdminTranslations(fetchedAdminTranslations)
        localStorage.setItem('ahpCustomTranslations', JSON.stringify(fetchedUserTranslations || {}))
        localStorage.setItem('ahpCustomAdminTranslations', JSON.stringify(fetchedAdminTranslations || {}))
    }

    useEffect(() => {
        if (activeView === 'users') {
            fetchUsers()
        } else if (activeView === 'studies') {
            fetchStudies()
            // Pobierz użytkowników dla dropdown
            const loadUsers = async () => {
                const usersList = await getUsers()
                setAvailableUsers(usersList)
            }
            loadUsers()
        } else if (activeView === 'translations') {
            loadCustomTranslations()
        } else if (activeView === 'responses') {
            fetchSubmissions()
        }
    }, [activeView])

    const fetchUsers = async () => {
        setLoading(true)
        const usersList = await getUsers()
        setUsers(usersList)
        setLoading(false)
    }

    const handleInputChange = (e, { name, value }) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleSubmit = async () => {
        if (!formData.name || !formData.email || !formData.password) {
            return
        }

        setSubmitting(true)
        try {
            await createUser(formData)
            setModalOpen(false)
            setFormData({ name: '', email: '', password: '', role: 'USER' })
            await fetchUsers()
        } catch (err) {
            // Error już obsłużony w AdminApi
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async (userId) => {
        if (window.confirm(t.admin.deleteConfirm)) {
            try {
                await deleteUser(userId)
                await fetchUsers()
            } catch (err) {
                // Error już obsłużony w AdminApi
            }
        }
    }

    const handleUserLanguageChange = async (userId, language) => {
        try {
            await updateUserLanguage(userId, language)
            await fetchUsers()
        } catch (err) {
            // Error już obsłużony w AdminApi
        }
    }

    const handleViewStudies = async (user) => {
        setSelectedUser(user)
        setStudiesModalOpen(true)
        setActiveUserStudyAccordionIndex(-1)
        setLoadingStudies(true)
        setUserSubmissions([])
        try {
            const [response, submissionsList] = await Promise.all([
                getUserStudies(user._id),
                getSubmissions({ userId: user._id })
            ])
            setUserStudies(response.studies || [])
            setActiveStudyIdForUser(response.activeStudyId ? String(response.activeStudyId) : null)
            setUserSubmissions(submissionsList)
        } catch (err) {
            // Error już obsłużony w AdminApi
        } finally {
            setLoadingStudies(false)
        }
    }

    const handleSetActiveStudy = async (userId, studyId) => {
        try {
            await updateUserActiveStudy(userId, studyId)
            setActiveStudyIdForUser(studyId)
            const activeStudy = userStudies.find((item) => String(item.study?._id) === String(studyId))
            setUsers((previousUsers) => previousUsers.map((user) => (
                String(user._id) === String(userId)
                    ? {
                        ...user,
                        activeStudyId: studyId,
                        activeStudyName: activeStudy?.study?.name || user.activeStudyName || null
                    }
                    : user
            )))
            setSelectedUser((previousUser) => (
                previousUser && String(previousUser._id) === String(userId)
                    ? {
                        ...previousUser,
                        activeStudyId: studyId,
                        activeStudyName: activeStudy?.study?.name || previousUser.activeStudyName || null
                    }
                    : previousUser
            ))
        } catch (err) {
            // Error już obsłużony w AdminApi
        }
    }

    // Studies functions
    const fetchStudies = async () => {
        setLoadingStudiesList(true)
        const studiesList = await getAllStudies()
        setStudies(studiesList)
        setLoadingStudiesList(false)
    }

    const fetchSubmissions = async (filters = {}) => {
        setLoadingResponses(true)
        const submissionsList = await getSubmissions(filters)
        setSubmissions(submissionsList)
        setLoadingResponses(false)
        return submissionsList
    }

    const openSubmission = (submission) => {
        setSelectedSubmission(submission)
        setSubmissionModalOpen(true)
    }

    const handleStudyInputChange = (e, { name, value }) => {
        setStudyFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const mapStudyToFormData = (rawStudy) => {
        const users = (rawStudy?.users || []).map((user) => (typeof user === 'object' ? user._id : user)).filter(Boolean)
        const marksByKey = (rawStudy?.marks || []).reduce((accumulator, mark) => {
            const key = mark.localKey || createLocalKey()
            accumulator[key] = { ...mark, localKey: key }
            return accumulator
        }, {})

        const pages = (rawStudy?.pages || []).length > 0
            ? rawStudy.pages.map((page) => ({
                type: page.type,
                title: page.title || '',
                text: page.text || '',
                categories: page.type === 'categories'
                    ? (() => {
                        const mapped = (page.markLocalKeys || []).map((key) => marksByKey[key]).filter(Boolean)
                        return mapped.length ? mapped : [createEmptyCategory()]
                    })()
                    : undefined
            }))
            : [createStartPage(), createEndPage()]

        return {
            name: rawStudy?.name || '',
            users,
            ...fromBreakDurationMs(rawStudy?.breakDurationMs),
            pages,
            defaultLanguage: rawStudy?.defaultLanguage || 'pl',
            translations: (rawStudy?.translations || []).map((translation) => alignStudyTranslation(translation, pages))
        }
    }

    const handleDownloadStudyJson = (studyData) => {
        const exportPayload = {
            ...studyData.study,
            _id: undefined,
            users: (studyData.study?.users || []).map((user) => (typeof user === 'object' ? user._id : user)).filter(Boolean)
        }

        const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' })
        const url = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `${(studyData.study?.name || 'study').replace(/\s+/g, '_')}.json`
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.URL.revokeObjectURL(url)
    }

    const handleExportSubmissionCsv = (submission) => {
        if (!submission) {
            return
        }
        const userPart = (submission.user?.name || submission.userName || 'user').replace(/\s+/g, '_')
        const studyPart = (submission.study?.name || submission.studyName || 'study').replace(/\s+/g, '_')
        const blob = new Blob([buildSubmissionCsv(submission)], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `submission_${userPart}_${studyPart}.csv`
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.URL.revokeObjectURL(url)
    }

    const handleImportStudyJson = async (event) => {
        const file = event.target.files && event.target.files[0]
        if (!file) {
            return
        }

        try {
            const content = await file.text()
            const parsed = JSON.parse(content)
            setStudyFormData(mapStudyToFormData(parsed))
            setActiveStudyPageTab(0)
            setStudyModalSection('content')
        } catch (error) {
            alert(t.admin.invalidStudyJson)
        } finally {
            event.target.value = ''
        }
    }

    const handleStudySubmit = async () => {
        if (!studyFormData.name || !studyFormData.users || studyFormData.users.length === 0) {
            return
        }

        const flatMarks = []
        const preparedPages = (studyFormData.pages || []).map((page) => {
            if (page.type !== 'categories') {
                return {
                    type: page.type,
                    text: page.text || '',
                    title: page.title || '',
                    markLocalKeys: []
                }
            }

            const categories = (page.categories || []).map((category) => {
                const namesList = (category.namesList || []).map((name) => name.trim()).filter(Boolean)
                const localKey = category.localKey || createLocalKey()
                const preparedCategory = {
                    ...category,
                    localKey,
                    title: category.title || '',
                    namesList,
                    results: buildResultsForOptions(namesList),
                    status: category.status || false
                }
                flatMarks.push(preparedCategory)
                return preparedCategory
            })

            return {
                type: 'categories',
                title: page.title || '',
                text: page.text || '',
                markLocalKeys: categories.map((category) => category.localKey)
            }
        })

        const preparedStudyData = {
            ...studyFormData,
            breakDurationMs: toBreakDurationMs(studyFormData.breakMinutes, studyFormData.breakSeconds),
            pages: preparedPages,
            marks: flatMarks,
            defaultLanguage: studyFormData.defaultLanguage || 'pl',
            translations: (studyFormData.translations || [])
                .map((translation) => alignStudyTranslation(translation, studyFormData.pages))
                .filter((translation) => translation.language && translation.language !== (studyFormData.defaultLanguage || 'pl'))
        }

        setSubmitting(true)
        try {
            if (editingStudy) {
                await updateStudy(editingStudy.study._id, preparedStudyData)
            } else {
                await createStudy(preparedStudyData)
            }
            setStudyModalOpen(false)
            setStudyFormData(createEmptyStudyFormData())
            setActiveStudyPageTab(0)
            setStudyModalSection('content')
            setActiveStudyTranslationLanguage('')
            setEditingStudy(null)
            await fetchStudies()
        } catch (err) {
            // Error już obsłużony w AdminApi
        } finally {
            setSubmitting(false)
        }
    }

    const handleEditStudy = (studyData) => {
        setEditingStudy(studyData)
        const formData = mapStudyToFormData(studyData.study)
        setStudyFormData(formData)
        setActiveStudyPageTab(0)
        setStudyModalSection('content')
        setActiveStudyTranslationLanguage(formData.translations?.[0]?.language || '')
        setStudyModalOpen(true)
    }

    const handleDeleteStudy = async (studyId) => {
        if (window.confirm(t.admin.deleteStudyConfirm)) {
            try {
                await deleteStudy(studyId)
                await fetchStudies()
            } catch (err) {
                // Error już obsłużony w AdminApi
            }
        }
    }

    const handleCreateStudy = () => {
        setEditingStudy(null)
        setStudyFormData(createEmptyStudyFormData())
        setActiveStudyPageTab(0)
        setStudyModalSection('content')
        setActiveStudyTranslationLanguage('')
        setStudyModalOpen(true)
    }

    const addStudyTranslation = (language) => {
        if (!language) {
            return
        }
        setStudyFormData((prev) => {
            if ((prev.translations || []).some((item) => item.language === language) || language === (prev.defaultLanguage || 'pl')) {
                return prev
            }
            const translations = [
                ...(prev.translations || []),
                alignStudyTranslation({ language, name: '', pages: [], marks: [] }, prev.pages)
            ]
            return { ...prev, translations }
        })
        setActiveStudyTranslationLanguage(language)
    }

    const removeStudyTranslation = (language) => {
        setStudyFormData((prev) => ({
            ...prev,
            translations: (prev.translations || []).filter((item) => item.language !== language)
        }))
        setActiveStudyTranslationLanguage((prev) => (prev === language ? '' : prev))
    }

    const updateStudyTranslation = (language, updater) => {
        setStudyFormData((prev) => ({
            ...prev,
            translations: (prev.translations || []).map((item) => (
                item.language === language ? updater(alignStudyTranslation(item, prev.pages)) : item
            ))
        }))
    }

    const getBuiltInTranslations = (scope) => (
        scope === 'admin' ? adminTranslations : userTranslations
    )

    const getCustomTranslationsForScope = (scope) => (
        scope === 'admin' ? customAdminTranslations : customUserTranslations
    )

    const getMergedTranslationForLanguage = (language, scope = 'user') => (
        deepMerge(getBuiltInTranslations(scope)?.[language] || {}, getCustomTranslationsForScope(scope)?.[language] || {})
    )

    const getReferenceTranslation = (scope = 'user') => (
        getBuiltInTranslations(scope)?.pl || getBuiltInTranslations(scope)?.en || {}
    )

    const extractLanguagePayload = (parsed, language, scope = 'user') => {
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return null
        }
        let payload = parsed
        const languagePayload = parsed[language]
        if (languagePayload && typeof languagePayload === 'object' && !Array.isArray(languagePayload)) {
            payload = languagePayload
        }
        if (scope === 'admin' && payload.admin && typeof payload.admin === 'object' && !Array.isArray(payload.admin) && !Object.prototype.hasOwnProperty.call(payload, 'title')) {
            payload = payload.admin
        }
        return payload
    }

    const validateUploadedTranslation = (translation, scope = 'user') => {
        const validation = validateTranslationKeys(translation, getReferenceTranslation(scope), scope)
        if (!validation.isValid) {
            setTranslationValidation({
                open: true,
                missing: validation.missing,
                extra: validation.extra
            })
        }
        return validation.isValid
    }

    const handleDownloadLanguageTranslations = (language, scope = 'user') => {
        const blob = new Blob([JSON.stringify(getMergedTranslationForLanguage(language, scope), null, 2)], { type: 'application/json' })
        const url = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `translations-${scope}-${language}.json`
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.URL.revokeObjectURL(url)
    }

    const handleUploadLanguageVersion = async (language, scope, event) => {
        const file = event.target.files && event.target.files[0]
        if (!file) {
            return
        }

        try {
            const parsed = JSON.parse(await file.text())
            const translation = extractLanguagePayload(parsed, language, scope)
            if (!translation || typeof translation !== 'object' || Array.isArray(translation)) {
                alert(t.admin.invalidTranslationsFile)
                return
            }
            if (!validateUploadedTranslation(translation, scope)) {
                return
            }
            await uploadLanguageVersion({ language, translation, scope })
            await loadCustomTranslations()
        } catch (error) {
            if (error instanceof SyntaxError) {
                alert(t.admin.invalidTranslationsFile)
            }
        } finally {
            event.target.value = ''
        }
    }

    const handleNewLanguageFile = async (event) => {
        const file = event.target.files && event.target.files[0]
        if (!file) {
            return
        }

        try {
            const content = await file.text()
            const parsed = JSON.parse(content)
            const language = (newLanguageData.language || '').trim().toLowerCase()
            const translation = language
                ? extractLanguagePayload(parsed, language, languageModalScope)
                : extractLanguagePayload(parsed, '', languageModalScope)
            if (!translation || typeof translation !== 'object' || Array.isArray(translation)) {
                alert(t.admin.invalidTranslationsFile)
                return
            }
            if (!validateUploadedTranslation(translation, languageModalScope)) {
                setNewLanguageData((prev) => ({
                    ...prev,
                    translation: null
                }))
                return
            }
            setNewLanguageData((prev) => ({
                ...prev,
                translation
            }))
        } catch (error) {
            if (error instanceof SyntaxError) {
                alert(t.admin.invalidTranslationsFile)
            }
        } finally {
            event.target.value = ''
        }
    }

    const handleCreateCustomLanguage = async () => {
        if (!newLanguageData.language || !newLanguageData.translation) {
            return
        }
        if (!validateUploadedTranslation(newLanguageData.translation, languageModalScope)) {
            return
        }
        try {
            await createCustomLanguage({
                language: newLanguageData.language,
                translation: newLanguageData.translation,
                scope: languageModalScope
            })
            setLanguageModalOpen(false)
            setNewLanguageData({ language: '', translation: null })
            await loadCustomTranslations()
        } catch (error) {
            // Error już obsłużony w AdminApi
        }
    }

    const handleDeleteCustomLanguage = async (language, scope) => {
        if (getBuiltInTranslations(scope)?.[language]) {
            alert(t.admin.cannotDeleteBuiltInLanguage)
            return
        }
        if (!window.confirm(t.admin.deleteCustomLanguageConfirm)) {
            return
        }
        try {
            await deleteCustomLanguage({ language, scope })
            await loadCustomTranslations()
        } catch (error) {
            // Error już obsłużony w AdminApi
        }
    }

    const getLanguageOptions = (scope) => (
        getLanguageDropdownOptions([
            ...Object.keys(getBuiltInTranslations(scope) || {}),
            ...Object.keys(getCustomTranslationsForScope(scope) || {})
        ])
    )

    const openLanguageModal = (scope) => {
        setLanguageModalScope(scope)
        setNewLanguageData({ language: '', translation: null })
        setLanguageModalOpen(true)
    }

    const renderTranslationsSection = (scope) => {
        const languageOptions = getLanguageOptions(scope)
        const sectionTitle = scope === 'admin'
            ? t.admin.adminPanelTranslationsTitle
            : t.admin.userViewTranslationsTitle

        return (
            <div style={{ marginBottom: 40 }}>
                <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Header as='h3'>{sectionTitle}</Header>
                    <Button
                        primary
                        icon='plus'
                        content={t.admin.addCustomLanguage}
                        onClick={() => openLanguageModal(scope)}
                    />
                </div>

                {languageOptions.length === 0 ? (
                    <Message warning>
                        <Message.Header>{t.admin.noUploadedTranslations}</Message.Header>
                        <p>{t.admin.noUploadedTranslationsMessage}</p>
                    </Message>
                ) : (
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{t.admin.translationLanguage}</Table.HeaderCell>
                                <Table.HeaderCell>{t.admin.actions}</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {languageOptions.map((languageOption) => (
                                <Table.Row key={`${scope}-${languageOption.value}`}>
                                    <Table.Cell>
                                        <LanguageLabel language={languageOption.value} />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Button
                                            size='small'
                                            icon='download'
                                            content={t.admin.downloadTranslations}
                                            onClick={() => handleDownloadLanguageTranslations(languageOption.value, scope)}
                                            style={{ marginRight: 5 }}
                                        />
                                        <Button
                                            as='label'
                                            htmlFor={`translation-version-upload-${scope}-${languageOption.value}`}
                                            size='small'
                                            icon='upload'
                                            content={t.admin.uploadNewTranslationVersion}
                                            style={{ marginRight: 0 }}
                                        />
                                        <input
                                            id={`translation-version-upload-${scope}-${languageOption.value}`}
                                            type='file'
                                            accept='application/json'
                                            onChange={(event) => handleUploadLanguageVersion(languageOption.value, scope, event)}
                                            style={{ display: 'none' }}
                                        />
                                        {!getBuiltInTranslations(scope)?.[languageOption.value] && (
                                            <Button
                                                negative
                                                size='small'
                                                icon='trash'
                                                content={t.admin.deleteCustomLanguage}
                                                onClick={() => handleDeleteCustomLanguage(languageOption.value, scope)}
                                                style={{ marginLeft: 5 }}
                                            />
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                )}
            </div>
        )
    }

    const renderSubmissionMarks = (marks = []) => {
        if (!marks.length) {
            return (
                <Message warning>
                    <Message.Header>{t.admin.noResponses}</Message.Header>
                    <p>{t.admin.noResponsesMessage}</p>
                </Message>
            )
        }

        return marks.map((mark, markIndex) => {
            const names = mark.namesList || []
            const ranking = [...(mark.ranking || [])].sort((a, b) => (b.score || 0) - (a.score || 0))
            return (
                <div key={mark.localKey || markIndex} style={{ marginTop: 15, padding: 10, border: '1px solid #ddd', borderRadius: 5 }}>
                    <strong>{mark.title || `${t.admin.mark} ${markIndex + 1}`}</strong>
                    {names.length > 0 && (
                        <div style={{ marginTop: 10, overflowX: 'auto' }}>
                            <strong>{t.admin.comparisonMatrix}:</strong>
                            <Table celled compact size='small' style={{ marginTop: 8 }}>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell />
                                        {names.map((name, index) => (
                                            <Table.HeaderCell key={`head-${markIndex}-${index}`}>{name}</Table.HeaderCell>
                                        ))}
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {names.map((rowName, rowIndex) => (
                                        <Table.Row key={`row-${markIndex}-${rowIndex}`}>
                                            <Table.Cell><strong>{rowName}</strong></Table.Cell>
                                            {names.map((colName, colIndex) => (
                                                <Table.Cell key={`cell-${markIndex}-${rowIndex}-${colIndex}`}>
                                                    {getPairwiseValue(rowIndex, colIndex, names.length, mark.results)}
                                                </Table.Cell>
                                            ))}
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </div>
                    )}
                    {typeof mark.cr === 'number' && (
                        <div style={{ marginTop: 8, color: mark.cr > 0.1 ? 'red' : 'green' }}>
                            <strong>{t.admin.consistencyRatio}:</strong> {mark.cr.toFixed(3)}
                        </div>
                    )}
                    {ranking.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                            <strong>{t.admin.ranking}:</strong>
                            <ol>
                                {ranking.map((item, rankIndex) => (
                                    <li key={`${item.name}-${rankIndex}`}>
                                        {item.name} — {t.admin.weight}: {Number(item.score || 0).toFixed(3)}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                </div>
            )
        })
    }

    // Przygotuj opcje użytkowników dla dropdown
    const userOptions = availableUsers.map(user => ({
        key: user._id,
        text: `${user.name} (${user.email})`,
        value: user._id
    }))
    const availableLanguageOptions = getLanguageDropdownOptions([
        ...Object.keys(userTranslations || {}),
        ...Object.keys(adminTranslations || {}),
        ...Object.keys(customUserTranslations || {}),
        ...Object.keys(customAdminTranslations || {})
    ])
    const catalogLanguageOptions = getCatalogLanguageOptions()

    return (
        <div style={{ backgroundColor: '#fcf5f5', minHeight: '100vh' }}>
            <Menu navigation={history} />
            
            <Segment style={{ margin: '20px auto', maxWidth: '1200px' }}>
                <Header as='h2' style={{ marginBottom: 20 }}>
                    <Icon name={activeView === 'users' ? 'users' : activeView === 'studies' ? 'file alternate' : activeView === 'responses' ? 'history' : 'language'} />
                    {t.admin.title}
                </Header>

                {/* Przełącznik widoków */}
                <UIMenu tabular style={{ marginBottom: 20 }}>
                    <UIMenu.Item
                        name='users'
                        active={activeView === 'users'}
                        onClick={() => setActiveView('users')}
                    >
                        <Icon name='users' />
                        {t.admin.usersView}
                    </UIMenu.Item>
                    <UIMenu.Item
                        name='studies'
                        active={activeView === 'studies'}
                        onClick={() => setActiveView('studies')}
                    >
                        <Icon name='file alternate' />
                        {t.admin.studiesView}
                    </UIMenu.Item>
                    <UIMenu.Item
                        name='responses'
                        active={activeView === 'responses'}
                        onClick={() => setActiveView('responses')}
                    >
                        <Icon name='history' />
                        {t.admin.responsesView}
                    </UIMenu.Item>
                    <UIMenu.Item
                        name='translations'
                        active={activeView === 'translations'}
                        onClick={() => setActiveView('translations')}
                    >
                        <Icon name='language' />
                        {t.admin.translationsView}
                    </UIMenu.Item>
                </UIMenu>

                {/* Widok użytkowników */}
                {activeView === 'users' && (
                    <>
                        <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Header as='h3'>{t.admin.usersList}</Header>
                            <Button 
                                primary 
                                icon='plus' 
                                content={t.admin.createUser} 
                                onClick={() => setModalOpen(true)}
                            />
                        </div>

                {loading ? (
                    <Message info>
                        <Message.Header>{t.admin.loading}</Message.Header>
                        <p>{t.admin.loading}</p>
                    </Message>
                ) : users.length === 0 ? (
                    <Message warning>
                        <Message.Header>{t.admin.noUsers}</Message.Header>
                        <p>{t.admin.noUsersMessage}</p>
                    </Message>
                ) : (
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{t.admin.userName}</Table.HeaderCell>
                                <Table.HeaderCell>{t.admin.userEmail}</Table.HeaderCell>
                                <Table.HeaderCell>{t.admin.userRole}</Table.HeaderCell>
                                <Table.HeaderCell>{t.admin.userLanguage}</Table.HeaderCell>
                                <Table.HeaderCell>{t.admin.studiesCount}</Table.HeaderCell>
                                <Table.HeaderCell>{t.admin.activeStudy}</Table.HeaderCell>
                                <Table.HeaderCell>{t.admin.actions}</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {users.map((user) => (
                                <Table.Row key={user._id}>
                                    <Table.Cell>{user.name}</Table.Cell>
                                    <Table.Cell>{user.email}</Table.Cell>
                                    <Table.Cell>{user.role || 'USER'}</Table.Cell>
                                    <Table.Cell>
                                        <Dropdown
                                            className='language-select'
                                            selection
                                            search
                                            options={availableLanguageOptions}
                                            value={user.language || 'pl'}
                                            onChange={(e, { value }) => handleUserLanguageChange(user._id, value)}
                                            placeholder='pl'
                                        />
                                    </Table.Cell>
                                    <Table.Cell>{user.studiesCount ?? user.studies?.length ?? 0}</Table.Cell>
                                    <Table.Cell>
                                        {user.activeStudyName
                                            ? <strong style={{ color: '#1b1c1d' }}>{user.activeStudyName}</strong>
                                            : <span style={{ color: '#999' }}>{t.admin.noActiveStudy}</span>}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Button 
                                            primary 
                                            size='small' 
                                            icon='eye' 
                                            content={t.admin.viewStudies}
                                            onClick={() => handleViewStudies(user)}
                                            style={{ marginRight: 5 }}
                                        />
                                        <Button 
                                            negative 
                                            size='small' 
                                            icon='trash' 
                                            content={t.admin.delete}
                                            onClick={() => handleDelete(user._id)}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                )}
                    </>
                )}

                {/* Widok badań */}
                {activeView === 'studies' && (
                    <>
                        <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Header as='h3'>{t.admin.studiesList}</Header>
                            <Button 
                                primary 
                                icon='plus' 
                                content={t.admin.createStudy} 
                                onClick={handleCreateStudy}
                            />
                        </div>

                        {loadingStudiesList ? (
                            <Message info>
                                <Message.Header>{t.admin.loading}</Message.Header>
                                <p>{t.admin.loadingStudies}</p>
                            </Message>
                        ) : studies.length === 0 ? (
                            <Message warning>
                                <Message.Header>{t.admin.noStudies}</Message.Header>
                                <p>{t.admin.noStudiesMessage}</p>
                            </Message>
                        ) : (
                            <Table celled>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>{t.admin.studyName}</Table.HeaderCell>
                                        <Table.HeaderCell>{t.admin.studyUsers}</Table.HeaderCell>
                                        <Table.HeaderCell>{t.admin.studyLanguages}</Table.HeaderCell>
                                        <Table.HeaderCell>{t.admin.breakTime}</Table.HeaderCell>
                                        <Table.HeaderCell>{t.admin.marksCount}</Table.HeaderCell>
                                        <Table.HeaderCell>{t.admin.actions}</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {studies.map((studyData) => (
                                        <Table.Row key={studyData.study?._id}>
                                            <Table.Cell>{studyData.study?.name || t.admin.unnamedStudy}</Table.Cell>
                                            <Table.Cell>
                                                {studyData.study?.users && studyData.study.users.length > 0
                                                    ? studyData.study.users.map((user, idx) => (
                                                        <span key={user._id || idx}>
                                                            {typeof user === 'object' ? user.name || user.email : user}
                                                            {idx < studyData.study.users.length - 1 ? ', ' : ''}
                                                        </span>
                                                    ))
                                                    : t.admin.noUsersAssigned
                                                }
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                                                    {getStudyLanguages(studyData.study).map((languageCode) => (
                                                        <LanguageLabel key={languageCode} language={languageCode} />
                                                    ))}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>{formatBreakDuration(studyData.study?.breakDurationMs)}</Table.Cell>
                                            <Table.Cell>{studyData.study?.marks?.length || 0}</Table.Cell>
                                            <Table.Cell>
                                                <Button 
                                                    primary 
                                                    size='small' 
                                                    icon='edit' 
                                                    content={t.admin.edit}
                                                    onClick={() => handleEditStudy(studyData)}
                                                    style={{ marginRight: 5 }}
                                                />
                                                <Button
                                                    size='small'
                                                    icon='download'
                                                    content={t.admin.downloadStudyJson}
                                                    onClick={() => handleDownloadStudyJson(studyData)}
                                                    style={{ marginRight: 5 }}
                                                />
                                                <Button 
                                                    negative 
                                                    size='small' 
                                                    icon='trash' 
                                                    content={t.admin.delete}
                                                    onClick={() => handleDeleteStudy(studyData.study._id)}
                                                />
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        )}
                    </>
                )}

                {activeView === 'responses' && (
                    <>
                        <div style={{ marginBottom: 20 }}>
                            <Header as='h3'>{t.admin.responsesList}</Header>
                        </div>
                        {loadingResponses ? (
                            <Message info>
                                <Message.Header>{t.admin.loading}</Message.Header>
                                <p>{t.admin.loadingResponses}</p>
                            </Message>
                        ) : submissions.length === 0 ? (
                            <Message warning>
                                <Message.Header>{t.admin.noResponses}</Message.Header>
                                <p>{t.admin.noResponsesMessage}</p>
                            </Message>
                        ) : (
                            <Table celled>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>{t.admin.userName}</Table.HeaderCell>
                                        <Table.HeaderCell>{t.admin.userEmail}</Table.HeaderCell>
                                        <Table.HeaderCell>{t.admin.studyName}</Table.HeaderCell>
                                        <Table.HeaderCell>{t.admin.submittedAt}</Table.HeaderCell>
                                        <Table.HeaderCell>{t.admin.updatedAt}</Table.HeaderCell>
                                        <Table.HeaderCell>{t.admin.actions}</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {submissions.map((submission) => (
                                        <Table.Row key={submission._id}>
                                            <Table.Cell>{submission.user?.name || submission.userName || '-'}</Table.Cell>
                                            <Table.Cell>{submission.user?.email || submission.userEmail || '-'}</Table.Cell>
                                            <Table.Cell>{submission.study?.name || submission.studyName || t.admin.unnamedStudy}</Table.Cell>
                                            <Table.Cell>{formatDateTime(submission.submittedAt)}</Table.Cell>
                                            <Table.Cell>{formatDateTime(submission.updatedAt)}</Table.Cell>
                                            <Table.Cell>
                                                <Button
                                                    primary
                                                    size='small'
                                                    icon='eye'
                                                    content={t.admin.viewSubmission}
                                                    onClick={() => openSubmission(submission)}
                                                />
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        )}
                    </>
                )}

                {activeView === 'translations' && (
                    <>
                        <Header as='h3' style={{ marginBottom: 30 }}>{t.admin.translationsSectionTitle}</Header>
                        {renderTranslationsSection('user')}
                        {renderTranslationsSection('admin')}
                    </>
                )}
            </Segment>

            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                closeOnDimmerClick={false}
                size='small'
            >
                <Header icon='user plus' content={t.admin.createUser} />
                <Modal.Content>
                    <Form>
                        <Form.Field>
                            <label>{t.admin.userName}</label>
                            <input
                                placeholder={t.admin.userName}
                                name='name'
                                value={formData.name}
                                onChange={(e) => handleInputChange(e, { name: 'name', value: e.target.value })}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>{t.admin.userEmail}</label>
                            <input
                                type='email'
                                placeholder={t.admin.userEmail}
                                name='email'
                                value={formData.email}
                                onChange={(e) => handleInputChange(e, { name: 'email', value: e.target.value })}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>{t.admin.userPassword}</label>
                            <input
                                type='password'
                                placeholder={t.admin.userPassword}
                                name='password'
                                value={formData.password}
                                onChange={(e) => handleInputChange(e, { name: 'password', value: e.target.value })}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>{t.admin.userRole}</label>
                            <Dropdown
                                selection
                                options={[
                                    { key: 'USER', text: t.admin.roleUser, value: 'USER' },
                                    { key: 'ADMIN', text: t.admin.roleAdmin, value: 'ADMIN' }
                                ]}
                                value={formData.role}
                                onChange={(e, { value }) => handleInputChange(e, { name: 'role', value })}
                            />
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setModalOpen(false)}>
                        {t.admin.cancel}
                    </Button>
                    <Button 
                        positive 
                        onClick={handleSubmit}
                        loading={submitting}
                        disabled={!formData.name || !formData.email || !formData.password || !formData.role}
                    >
                        {t.admin.create}
                    </Button>
                </Modal.Actions>
            </Modal>

            {/* Modal do przeglądania badań użytkownika */}
            <Modal
                open={studiesModalOpen}
                onClose={() => {
                    setStudiesModalOpen(false)
                    setSelectedUser(null)
                    setUserStudies([])
                    setActiveStudyIdForUser(null)
                    setActiveUserStudyAccordionIndex(-1)
                    setUserSubmissions([])
                }}
                closeOnDimmerClick={false}
                size='large'
            >
                <Header icon='file alternate' content={t.admin.userStudies + (selectedUser ? ` - ${selectedUser.name}` : '')} />
                <Modal.Content>
                    {loadingStudies ? (
                        <Message info>
                            <Message.Header>{t.admin.loading}</Message.Header>
                            <p>{t.admin.loadingStudies}</p>
                        </Message>
                    ) : userStudies.length === 0 ? (
                        <Message warning>
                            <Message.Header>{t.admin.noStudies}</Message.Header>
                            <p>{t.admin.noStudiesMessage}</p>
                        </Message>
                    ) : (
                        <Accordion styled>
                            {userStudies.map((studyData, index) => {
                                const studyId = studyData.study?._id
                                const isActiveStudy = String(activeStudyIdForUser || '') === String(studyId || '')
                                return (
                                <div key={studyId || index}>
                                    <Accordion.Title
                                        active={activeUserStudyAccordionIndex === index}
                                        index={index}
                                        onClick={() => setActiveUserStudyAccordionIndex((prev) => prev === index ? -1 : index)}
                                        style={{
                                            color: isActiveStudy ? '#1b1c1d' : '#767676',
                                            fontWeight: isActiveStudy ? 700 : 400,
                                            backgroundColor: isActiveStudy ? '#f3f4f5' : undefined
                                        }}
                                    >
                                        <Icon name='dropdown' />
                                        <Radio
                                            checked={isActiveStudy}
                                            onClick={(event) => event.stopPropagation()}
                                            onChange={(event) => {
                                                event.stopPropagation()
                                                if (selectedUser && studyId) {
                                                    handleSetActiveStudy(selectedUser._id, studyId)
                                                }
                                            }}
                                            style={{ marginRight: 10 }}
                                        />
                                        {t.admin.study} {index + 1}: {studyData.study?.name || t.admin.unnamedStudy}
                                        {isActiveStudy && (
                                            <span style={{
                                                marginLeft: 10,
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: '#1b1c1d',
                                                textTransform: 'uppercase'
                                            }}>
                                                ({t.admin.activeStudyBadge})
                                            </span>
                                        )}
                                    </Accordion.Title>
                                    <Accordion.Content active={activeUserStudyAccordionIndex === index}>
                                        <div style={{ marginBottom: 20 }}>
                                            <strong>{t.admin.studyName}:</strong> {studyData.study?.name || t.admin.unnamedStudy}
                                        </div>
                                        <div style={{ marginBottom: 20 }}>
                                            <Radio
                                                label={t.admin.setAsActiveStudy}
                                                checked={isActiveStudy}
                                                onChange={() => selectedUser && studyId && handleSetActiveStudy(selectedUser._id, studyId)}
                                            />
                                        </div>
                                        <div style={{ marginBottom: 20 }}>
                                            <strong>{t.admin.submissionHistory}:</strong>
                                            {(() => {
                                                const studySubmissions = userSubmissions.filter((submission) => (
                                                    String(getSubmissionStudyId(submission)) === String(studyData.study?._id)
                                                ))
                                                if (!studySubmissions.length) {
                                                    return (
                                                        <Message warning size='small' style={{ marginTop: 10 }}>
                                                            <Message.Header>{t.admin.noSubmissionHistory}</Message.Header>
                                                            <p>{t.admin.noSubmissionHistoryMessage}</p>
                                                        </Message>
                                                    )
                                                }
                                                return (
                                                    <Table celled compact size='small' style={{ marginTop: 10 }}>
                                                        <Table.Header>
                                                            <Table.Row>
                                                                <Table.HeaderCell>{t.admin.submittedAt}</Table.HeaderCell>
                                                                <Table.HeaderCell>{t.admin.updatedAt}</Table.HeaderCell>
                                                                <Table.HeaderCell>{t.admin.actions}</Table.HeaderCell>
                                                            </Table.Row>
                                                        </Table.Header>
                                                        <Table.Body>
                                                            {studySubmissions.map((submission) => (
                                                                <Table.Row key={submission._id}>
                                                                    <Table.Cell>{formatDateTime(submission.submittedAt)}</Table.Cell>
                                                                    <Table.Cell>{formatDateTime(submission.updatedAt)}</Table.Cell>
                                                                    <Table.Cell>
                                                                        <Button
                                                                            primary
                                                                            size='tiny'
                                                                            icon='eye'
                                                                            content={t.admin.viewSubmission}
                                                                            onClick={() => openSubmission(submission)}
                                                                        />
                                                                    </Table.Cell>
                                                                </Table.Row>
                                                            ))}
                                                        </Table.Body>
                                                    </Table>
                                                )
                                            })()}
                                        </div>
                                        {studyData.study?.marks && studyData.study.marks.length > 0 && (
                                            <div>
                                                <strong>{t.admin.marks}:</strong>
                                                {studyData.study.marks.map((mark, markIndex) => (
                                                    <div key={markIndex} style={{ marginTop: 15, padding: 10, border: '1px solid #ddd', borderRadius: 5 }}>
                                                        <strong>{t.admin.mark} {markIndex + 1}:</strong>
                                                        {mark.namesList && mark.namesList.length > 0 && (
                                                            <div style={{ marginTop: 5 }}>
                                                                <strong>{t.admin.options}:</strong>
                                                                <ul>
                                                                    {mark.namesList.map((name, nameIndex) => (
                                                                        <li key={nameIndex}>{name}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        {mark.results && mark.results.length > 0 && (
                                                            <div style={{ marginTop: 5 }}>
                                                                <strong>{t.admin.results}:</strong> {mark.results.length} {t.admin.comparisons}
                                                            </div>
                                                        )}
                                                        <div style={{ marginTop: 5 }}>
                                                            <strong>{t.admin.status}:</strong> {mark.status ? t.admin.completed : t.admin.incomplete}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Accordion.Content>
                                </div>
                                )
                            })}
                        </Accordion>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => {
                        setStudiesModalOpen(false)
                        setSelectedUser(null)
                        setUserStudies([])
                        setActiveStudyIdForUser(null)
                        setActiveUserStudyAccordionIndex(-1)
                        setUserSubmissions([])
                    }}>
                        {t.admin.close}
                    </Button>
                </Modal.Actions>
            </Modal>

            <Modal
                open={submissionModalOpen}
                onClose={() => {
                    setSubmissionModalOpen(false)
                    setSelectedSubmission(null)
                }}
                closeOnDimmerClick={false}
                size='large'
            >
                <Header icon='history' content={t.admin.submissionDetails} />
                <Modal.Content>
                    {selectedSubmission ? (
                        <>
                            <div style={{ marginBottom: 10 }}>
                                <strong>{t.admin.userName}:</strong> {selectedSubmission.user?.name || selectedSubmission.userName || '-'}
                            </div>
                            <div style={{ marginBottom: 10 }}>
                                <strong>{t.admin.userEmail}:</strong> {selectedSubmission.user?.email || selectedSubmission.userEmail || '-'}
                            </div>
                            <div style={{ marginBottom: 10 }}>
                                <strong>{t.admin.studyName}:</strong> {selectedSubmission.study?.name || selectedSubmission.studyName || t.admin.unnamedStudy}
                            </div>
                            <div style={{ marginBottom: 10 }}>
                                <strong>{t.admin.submittedAt}:</strong> {formatDateTime(selectedSubmission.submittedAt)}
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <strong>{t.admin.updatedAt}:</strong> {formatDateTime(selectedSubmission.updatedAt)}
                            </div>
                            {renderSubmissionMarks(selectedSubmission.marks)}
                        </>
                    ) : (
                        <Message warning>
                            <Message.Header>{t.admin.noResponses}</Message.Header>
                            <p>{t.admin.noResponsesMessage}</p>
                        </Message>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        primary
                        icon='download'
                        content={t.admin.exportSubmissionCsv}
                        disabled={!selectedSubmission}
                        onClick={() => handleExportSubmissionCsv(selectedSubmission)}
                    />
                    <Button onClick={() => {
                        setSubmissionModalOpen(false)
                        setSelectedSubmission(null)
                    }}>
                        {t.admin.close}
                    </Button>
                </Modal.Actions>
            </Modal>

            {/* Modal do tworzenia/edycji badania */}
            <Modal
                open={studyModalOpen}
                onClose={() => {
                    setStudyModalOpen(false)
                    setEditingStudy(null)
                    setStudyFormData(createEmptyStudyFormData())
                    setActiveStudyPageTab(0)
                    setStudyModalSection('content')
                    setActiveStudyTranslationLanguage('')
                }}
                closeOnDimmerClick={false}
                size='large'
            >
                <Header 
                    icon='file alternate' 
                    content={editingStudy ? t.admin.editStudy : t.admin.createStudy} 
                />
                <Modal.Content>
                    <Form>
                        <Form.Field>
                            <label>{t.admin.studyName}</label>
                            <input
                                placeholder={t.admin.studyNamePlaceholder}
                                name='name'
                                value={studyFormData.name}
                                onChange={(e) => handleStudyInputChange(e, { name: 'name', value: e.target.value })}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>{t.admin.defaultLanguage}</label>
                            <Dropdown
                                className='language-select'
                                selection
                                search
                                options={catalogLanguageOptions}
                                value={studyFormData.defaultLanguage || 'pl'}
                                onChange={(e, { value }) => {
                                    setStudyFormData((prev) => ({
                                        ...prev,
                                        defaultLanguage: value,
                                        translations: (prev.translations || []).filter((item) => item.language !== value)
                                    }))
                                    if (activeStudyTranslationLanguage === value) {
                                        setActiveStudyTranslationLanguage('')
                                    }
                                }}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>{t.admin.studyUsers}</label>
                            <Dropdown
                                placeholder={t.admin.selectUsers}
                                fluid
                                multiple
                                selection
                                options={userOptions}
                                value={studyFormData.users}
                                onChange={(e, { value }) => handleStudyInputChange(e, { name: 'users', value })}
                            />
                        </Form.Field>
                        <Form.Group widths='equal'>
                            <Form.Input
                                type='number'
                                min={0}
                                label={t.admin.breakMinutes}
                                name='breakMinutes'
                                value={studyFormData.breakMinutes}
                                onChange={(e) => handleStudyInputChange(e, { name: 'breakMinutes', value: e.target.value })}
                            />
                            <Form.Input
                                type='number'
                                min={0}
                                max={59}
                                label={t.admin.breakSeconds}
                                name='breakSeconds'
                                value={studyFormData.breakSeconds}
                                onChange={(e) => handleStudyInputChange(e, { name: 'breakSeconds', value: e.target.value })}
                            />
                        </Form.Group>
                        {!editingStudy && (
                            <Form.Field>
                                <Button
                                    type='button'
                                    as='label'
                                    htmlFor='study-import-json-input'
                                    icon='upload'
                                    content={t.admin.importStudyJson}
                                />
                                <input
                                    id='study-import-json-input'
                                    type='file'
                                    accept='application/json'
                                    onChange={handleImportStudyJson}
                                    style={{ display: 'none' }}
                                />
                            </Form.Field>
                        )}
                        <UIMenu tabular style={{ marginBottom: 20 }}>
                            <UIMenu.Item
                                active={studyModalSection === 'content'}
                                onClick={() => setStudyModalSection('content')}
                            >
                                {t.admin.studyContentTab}
                            </UIMenu.Item>
                            <UIMenu.Item
                                active={studyModalSection === 'translations'}
                                onClick={() => {
                                    setStudyFormData((prev) => ({
                                        ...prev,
                                        translations: (prev.translations || []).map((item) => alignStudyTranslation(item, prev.pages))
                                    }))
                                    setStudyModalSection('translations')
                                    setActiveStudyTranslationLanguage((prev) => prev || studyFormData.translations?.[0]?.language || '')
                                }}
                            >
                                {t.admin.studyTranslationsTab}
                            </UIMenu.Item>
                        </UIMenu>
                        {studyModalSection === 'content' && (
                        <>
                        <UIMenu tabular style={{ marginBottom: 20 }}>
                            {(studyFormData.pages || []).map((page, pageIdx) => (
                                <UIMenu.Item
                                    key={`study-page-${pageIdx}`}
                                    active={activeStudyPageTab === pageIdx}
                                    onClick={() => setActiveStudyPageTab(pageIdx)}
                                >
                                    {`${t.admin.pageLabel} ${pageIdx + 1}: ${t.admin[`pageType${page.type.charAt(0).toUpperCase() + page.type.slice(1)}`] || page.type}`}
                                </UIMenu.Item>
                            ))}
                        </UIMenu>

                        <div style={{ display: 'flex', gap: 10, marginBottom: 15 }}>
                            <Button
                                type='button'
                                icon='pause'
                                content={t.admin.addBreakPage}
                                onClick={() => {
                                    setStudyFormData((prev) => {
                                        const pages = [...(prev.pages || [])]
                                        pages.splice(Math.max(1, pages.length - 1), 0, createBreakPage())
                                        return { ...prev, pages }
                                    })
                                    setActiveStudyPageTab(Math.max(1, (studyFormData.pages || []).length - 1))
                                }}
                            />
                            <Button
                                type='button'
                                icon='list'
                                content={t.admin.addCategoriesPage}
                                onClick={() => {
                                    setStudyFormData((prev) => {
                                        const pages = [...(prev.pages || [])]
                                        pages.splice(Math.max(1, pages.length - 1), 0, createCategoriesPage())
                                        return { ...prev, pages }
                                    })
                                    setActiveStudyPageTab(Math.max(1, (studyFormData.pages || []).length - 1))
                                }}
                            />
                            {activeStudyPageTab > 0 && activeStudyPageTab < (studyFormData.pages || []).length - 1 && (
                                <>
                                    <Button
                                        type='button'
                                        icon='arrow left'
                                        content={t.admin.movePageLeft}
                                        disabled={activeStudyPageTab <= 1}
                                        onClick={() => {
                                            setStudyFormData((prev) => {
                                                const pages = [...(prev.pages || [])]
                                                const currentIndex = activeStudyPageTab
                                                const targetIndex = currentIndex - 1
                                                const temp = pages[targetIndex]
                                                pages[targetIndex] = pages[currentIndex]
                                                pages[currentIndex] = temp
                                                return { ...prev, pages }
                                            })
                                            setActiveStudyPageTab((prev) => Math.max(1, prev - 1))
                                        }}
                                    />
                                    <Button
                                        type='button'
                                        icon='arrow right'
                                        content={t.admin.movePageRight}
                                        disabled={activeStudyPageTab >= (studyFormData.pages || []).length - 2}
                                        onClick={() => {
                                            setStudyFormData((prev) => {
                                                const pages = [...(prev.pages || [])]
                                                const currentIndex = activeStudyPageTab
                                                const targetIndex = currentIndex + 1
                                                const temp = pages[targetIndex]
                                                pages[targetIndex] = pages[currentIndex]
                                                pages[currentIndex] = temp
                                                return { ...prev, pages }
                                            })
                                            setActiveStudyPageTab((prev) => prev + 1)
                                        }}
                                    />
                                    <Button
                                        type='button'
                                        negative
                                        icon='trash'
                                        content={t.admin.deletePage}
                                        onClick={() => {
                                            setStudyFormData((prev) => {
                                                const pages = [...(prev.pages || [])]
                                                pages.splice(activeStudyPageTab, 1)
                                                return { ...prev, pages }
                                            })
                                            setActiveStudyPageTab((prev) => Math.max(0, prev - 1))
                                        }}
                                    />
                                </>
                            )}
                        </div>

                        {(() => {
                            const page = (studyFormData.pages || [])[activeStudyPageTab]
                            if (!page) return null

                            if (page.type === 'start' || page.type === 'break' || page.type === 'end') {
                                const hasCustomText = !isEmptyHtmlContent(page.text)
                                const defaultPageText = getDefaultPageText(page.type)
                                return (
                                    <Form.Field>
                                        <label>{t.admin.pageText}</label>
                                        <SimpleHtmlEditor
                                            placeholder={t.admin.pageTextPlaceholder}
                                            value={page.text || ''}
                                            onChange={(html) => {
                                                setStudyFormData((prev) => {
                                                    const pages = [...(prev.pages || [])]
                                                    pages[activeStudyPageTab] = { ...pages[activeStudyPageTab], text: html }
                                                    return { ...prev, pages }
                                                })
                                            }}
                                        />
                                        {!hasCustomText && (
                                            <Message info size='small' style={{ marginTop: 10 }}>
                                                <Message.Header>{t.admin.defaultPageTextHint}</Message.Header>
                                                <div
                                                    style={{ marginTop: 8 }}
                                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(defaultPageText) }}
                                                />
                                            </Message>
                                        )}
                                    </Form.Field>
                                )
                            }

                            return (
                                <>
                                    <Form.Field>
                                        <label>{t.admin.categoriesPageTitle}</label>
                                        <input
                                            placeholder={t.admin.categoriesPageTitlePlaceholder}
                                            value={page.title || ''}
                                            onChange={(e) => {
                                                setStudyFormData((prev) => {
                                                    const pages = [...(prev.pages || [])]
                                                    pages[activeStudyPageTab] = { ...pages[activeStudyPageTab], title: e.target.value }
                                                    return { ...prev, pages }
                                                })
                                            }}
                                        />
                                    </Form.Field>
                                    {(page.categories || []).map((category, categoryIndex) => (
                                        <div key={category.localKey || categoryIndex} style={{ marginBottom: 15, padding: 10, border: '1px solid #ddd', borderRadius: 5 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                <strong>{t.admin.mark} {categoryIndex + 1}</strong>
                                                {(page.categories || []).length > 1 && (
                                                    <Button
                                                        type='button'
                                                        negative
                                                        size='tiny'
                                                        icon='trash'
                                                        onClick={() => {
                                                            setStudyFormData((prev) => {
                                                                const pages = [...(prev.pages || [])]
                                                                const categories = [...(pages[activeStudyPageTab].categories || [])]
                                                                categories.splice(categoryIndex, 1)
                                                                pages[activeStudyPageTab] = { ...pages[activeStudyPageTab], categories }
                                                                return { ...prev, pages }
                                                            })
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <div style={{ marginTop: 10 }}>
                                                <label>{t.admin.markTitle}:</label>
                                                <input
                                                    placeholder={t.admin.markTitlePlaceholder}
                                                    value={category.title || ''}
                                                    onChange={(e) => {
                                                        setStudyFormData((prev) => {
                                                            const pages = [...(prev.pages || [])]
                                                            const categories = [...(pages[activeStudyPageTab].categories || [])]
                                                            categories[categoryIndex] = { ...categories[categoryIndex], title: e.target.value }
                                                            pages[activeStudyPageTab] = { ...pages[activeStudyPageTab], categories }
                                                            return { ...prev, pages }
                                                        })
                                                    }}
                                                />
                                            </div>
                                            <div style={{ marginTop: 10 }}>
                                                <label>{t.admin.options}:</label>
                                                {(category.namesList || []).map((optionValue, optionIndex) => (
                                                    <div key={`${category.localKey}-${optionIndex}`} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                        <input
                                                            style={{ flex: 1 }}
                                                            placeholder={`${t.admin.optionLabel} ${optionIndex + 1}`}
                                                            value={optionValue}
                                                            onChange={(e) => {
                                                                setStudyFormData((prev) => {
                                                                    const pages = [...(prev.pages || [])]
                                                                    const categories = [...(pages[activeStudyPageTab].categories || [])]
                                                                    const namesList = [...(categories[categoryIndex].namesList || [])]
                                                                    namesList[optionIndex] = e.target.value
                                                                    categories[categoryIndex] = { ...categories[categoryIndex], namesList }
                                                                    pages[activeStudyPageTab] = { ...pages[activeStudyPageTab], categories }
                                                                    return { ...prev, pages }
                                                                })
                                                            }}
                                                        />
                                                        <Button
                                                            type='button'
                                                            negative
                                                            size='tiny'
                                                            icon='trash'
                                                            onClick={() => {
                                                                setStudyFormData((prev) => {
                                                                    const pages = [...(prev.pages || [])]
                                                                    const categories = [...(pages[activeStudyPageTab].categories || [])]
                                                                    const namesList = [...(categories[categoryIndex].namesList || [])]
                                                                    namesList.splice(optionIndex, 1)
                                                                    categories[categoryIndex] = { ...categories[categoryIndex], namesList }
                                                                    pages[activeStudyPageTab] = { ...pages[activeStudyPageTab], categories }
                                                                    return { ...prev, pages }
                                                                })
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                                <Button
                                                    type='button'
                                                    size='tiny'
                                                    icon='plus'
                                                    content={t.admin.addOption}
                                                    onClick={() => {
                                                        setStudyFormData((prev) => {
                                                            const pages = [...(prev.pages || [])]
                                                            const categories = [...(pages[activeStudyPageTab].categories || [])]
                                                            const namesList = [...(categories[categoryIndex].namesList || []), '']
                                                            categories[categoryIndex] = { ...categories[categoryIndex], namesList }
                                                            pages[activeStudyPageTab] = { ...pages[activeStudyPageTab], categories }
                                                            return { ...prev, pages }
                                                        })
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <Button
                                        type='button'
                                        icon='plus'
                                        content={t.admin.addMark}
                                        onClick={() => {
                                            setStudyFormData((prev) => {
                                                const pages = [...(prev.pages || [])]
                                                const categories = [...(pages[activeStudyPageTab].categories || []), createEmptyCategory()]
                                                pages[activeStudyPageTab] = { ...pages[activeStudyPageTab], categories }
                                                return { ...prev, pages }
                                            })
                                        }}
                                    />
                                </>
                            )
                        })()}
                        </>
                        )}
                        {studyModalSection === 'translations' && (() => {
                            const usedLanguages = [
                                studyFormData.defaultLanguage || 'pl',
                                ...(studyFormData.translations || []).map((item) => item.language)
                            ]
                            const languageToAddOptions = getCatalogLanguageOptions({ exclude: usedLanguages })
                            const selectedLanguage = activeStudyTranslationLanguage || studyFormData.translations?.[0]?.language || ''
                            const currentTranslation = (studyFormData.translations || []).find((item) => item.language === selectedLanguage)
                            const alignedTranslation = currentTranslation
                                ? alignStudyTranslation(currentTranslation, studyFormData.pages)
                                : null
                            const translationPage = alignedTranslation?.pages?.[activeStudyPageTab] || { title: '', text: '' }
                            const sourcePage = (studyFormData.pages || [])[activeStudyPageTab]

                            return (
                                <>
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 15, flexWrap: 'wrap' }}>
                                        <Form.Field style={{ minWidth: 180 }}>
                                            <label>{t.admin.selectTranslationLanguage}</label>
                                            <Dropdown
                                                className='language-select'
                                                selection
                                                search
                                                placeholder={t.admin.selectTranslationLanguage}
                                                options={languageToAddOptions}
                                                onChange={(e, { value }) => addStudyTranslation(value)}
                                                key={`add-study-translation-${(studyFormData.translations || []).length}`}
                                                disabled={languageToAddOptions.length === 0}
                                            />
                                        </Form.Field>
                                    </div>
                                    {(studyFormData.translations || []).length === 0 ? (
                                        <Message info>
                                            <Message.Header>{t.admin.noStudyTranslations}</Message.Header>
                                            <p>{t.admin.noStudyTranslationsMessage}</p>
                                        </Message>
                                    ) : (
                                        <>
                                            <UIMenu tabular style={{ marginBottom: 15 }}>
                                                {(studyFormData.translations || []).map((item) => (
                                                    <UIMenu.Item
                                                        key={`study-translation-${item.language}`}
                                                        active={selectedLanguage === item.language}
                                                        onClick={() => setActiveStudyTranslationLanguage(item.language)}
                                                    >
                                                        <LanguageLabel language={item.language} />
                                                    </UIMenu.Item>
                                                ))}
                                            </UIMenu>
                                            {alignedTranslation && (
                                                <>
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                                                        <Button
                                                            type='button'
                                                            negative
                                                            size='tiny'
                                                            icon='trash'
                                                            content={t.admin.deleteStudyTranslation}
                                                            onClick={() => removeStudyTranslation(alignedTranslation.language)}
                                                        />
                                                    </div>
                                                    <Form.Field>
                                                        <label>{t.admin.studyName}</label>
                                                        <input
                                                            placeholder={studyFormData.name}
                                                            value={alignedTranslation.name || ''}
                                                            onChange={(e) => updateStudyTranslation(alignedTranslation.language, (item) => ({
                                                                ...item,
                                                                name: e.target.value
                                                            }))}
                                                        />
                                                    </Form.Field>
                                                    <UIMenu tabular style={{ marginBottom: 15 }}>
                                                        {(studyFormData.pages || []).map((page, pageIdx) => (
                                                            <UIMenu.Item
                                                                key={`study-translation-page-${pageIdx}`}
                                                                active={activeStudyPageTab === pageIdx}
                                                                onClick={() => setActiveStudyPageTab(pageIdx)}
                                                            >
                                                                {`${t.admin.pageLabel} ${pageIdx + 1}: ${t.admin[`pageType${page.type.charAt(0).toUpperCase() + page.type.slice(1)}`] || page.type}`}
                                                            </UIMenu.Item>
                                                        ))}
                                                    </UIMenu>
                                                    {sourcePage && (sourcePage.type === 'start' || sourcePage.type === 'break' || sourcePage.type === 'end') && (
                                                        <Form.Field>
                                                            <label>{t.admin.pageText}</label>
                                                            <SimpleHtmlEditor
                                                                placeholder={sourcePage.text || t.admin.pageTextPlaceholder}
                                                                value={translationPage.text || ''}
                                                                onChange={(html) => updateStudyTranslation(alignedTranslation.language, (item) => {
                                                                    const pages = [...(item.pages || [])]
                                                                    pages[activeStudyPageTab] = { ...(pages[activeStudyPageTab] || {}), text: html }
                                                                    return { ...item, pages }
                                                                })}
                                                            />
                                                        </Form.Field>
                                                    )}
                                                    {sourcePage && sourcePage.type === 'categories' && (
                                                        <>
                                                            <Form.Field>
                                                                <label>{t.admin.categoriesPageTitle}</label>
                                                                <input
                                                                    placeholder={sourcePage.title || t.admin.categoriesPageTitlePlaceholder}
                                                                    value={translationPage.title || ''}
                                                                    onChange={(e) => updateStudyTranslation(alignedTranslation.language, (item) => {
                                                                        const pages = [...(item.pages || [])]
                                                                        pages[activeStudyPageTab] = { ...(pages[activeStudyPageTab] || {}), title: e.target.value }
                                                                        return { ...item, pages }
                                                                    })}
                                                                />
                                                            </Form.Field>
                                                            {(sourcePage.categories || []).map((category, categoryIndex) => {
                                                                const markTranslation = (alignedTranslation.marks || []).find((mark) => mark.localKey === category.localKey) || {
                                                                    localKey: category.localKey,
                                                                    title: '',
                                                                    namesList: (category.namesList || []).map(() => '')
                                                                }
                                                                return (
                                                                    <div key={category.localKey || categoryIndex} style={{ marginBottom: 15, padding: 10, border: '1px solid #ddd', borderRadius: 5 }}>
                                                                        <strong>{t.admin.mark} {categoryIndex + 1}</strong>
                                                                        <div style={{ marginTop: 10 }}>
                                                                            <label>{t.admin.markTitle}:</label>
                                                                            <input
                                                                                placeholder={category.title || t.admin.markTitlePlaceholder}
                                                                                value={markTranslation.title || ''}
                                                                                onChange={(e) => updateStudyTranslation(alignedTranslation.language, (item) => ({
                                                                                    ...item,
                                                                                    marks: (item.marks || []).map((mark) => (
                                                                                        mark.localKey === category.localKey
                                                                                            ? { ...mark, title: e.target.value }
                                                                                            : mark
                                                                                    ))
                                                                                }))}
                                                                            />
                                                                        </div>
                                                                        <div style={{ marginTop: 10 }}>
                                                                            <label>{t.admin.options}:</label>
                                                                            {(category.namesList || []).map((optionValue, optionIndex) => (
                                                                                <input
                                                                                    key={`${category.localKey}-${optionIndex}`}
                                                                                    style={{ display: 'block', width: '100%', marginBottom: 8 }}
                                                                                    placeholder={optionValue || `${t.admin.optionLabel} ${optionIndex + 1}`}
                                                                                    value={markTranslation.namesList?.[optionIndex] || ''}
                                                                                    onChange={(e) => updateStudyTranslation(alignedTranslation.language, (item) => ({
                                                                                        ...item,
                                                                                        marks: (item.marks || []).map((mark) => {
                                                                                            if (mark.localKey !== category.localKey) {
                                                                                                return mark
                                                                                            }
                                                                                            const namesList = [...(mark.namesList || [])]
                                                                                            namesList[optionIndex] = e.target.value
                                                                                            return { ...mark, namesList }
                                                                                        })
                                                                                    }))}
                                                                                />
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )
                        })()}
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => {
                        setStudyModalOpen(false)
                        setEditingStudy(null)
                        setStudyFormData(createEmptyStudyFormData())
                        setActiveStudyPageTab(0)
                        setStudyModalSection('content')
                        setActiveStudyTranslationLanguage('')
                    }}>
                        {t.admin.cancel}
                    </Button>
                    <Button 
                        positive 
                        onClick={handleStudySubmit}
                        loading={submitting}
                        disabled={!studyFormData.name || !studyFormData.users || studyFormData.users.length === 0}
                    >
                        {editingStudy ? t.admin.update : t.admin.create}
                    </Button>
                </Modal.Actions>
            </Modal>

            <Modal
                open={languageModalOpen}
                onClose={() => {
                    setLanguageModalOpen(false)
                    setNewLanguageData({ language: '', translation: null })
                }}
                closeOnDimmerClick={false}
                size='small'
            >
                <Header
                    icon='language'
                    content={`${t.admin.addCustomLanguage} — ${languageModalScope === 'admin' ? t.admin.adminPanelTranslationsTitle : t.admin.userViewTranslationsTitle}`}
                />
                <Modal.Content>
                    <Form>
                        <Form.Field>
                            <label>{t.admin.translationLanguageName}</label>
                            <Dropdown
                                className='language-select'
                                selection
                                search
                                fluid
                                placeholder={t.admin.translationLanguageName}
                                options={getCatalogLanguageOptions({
                                    exclude: [
                                        ...Object.keys(getBuiltInTranslations(languageModalScope) || {}),
                                        ...Object.keys(getCustomTranslationsForScope(languageModalScope) || {})
                                    ]
                                })}
                                value={newLanguageData.language || null}
                                onChange={(e, { value }) => setNewLanguageData((prev) => ({
                                    ...prev,
                                    language: value
                                }))}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>{t.admin.uploadLanguageFile}</label>
                            <Button
                                as='label'
                                htmlFor='custom-language-upload-input'
                                icon='upload'
                                content={t.admin.uploadTranslations}
                            />
                            <input
                                id='custom-language-upload-input'
                                type='file'
                                accept='application/json'
                                onChange={handleNewLanguageFile}
                                style={{ display: 'none' }}
                            />
                            {newLanguageData.translation && (
                                <Message positive size='small' style={{ marginTop: 10 }}>
                                    {t.admin.translationFileReady}
                                </Message>
                            )}
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => {
                        setLanguageModalOpen(false)
                        setNewLanguageData({ language: '', translation: null })
                    }}>
                        {t.admin.cancel}
                    </Button>
                    <Button
                        positive
                        onClick={handleCreateCustomLanguage}
                        disabled={!newLanguageData.language || !newLanguageData.translation}
                    >
                        {t.admin.create}
                    </Button>
                </Modal.Actions>
            </Modal>

            <Modal
                open={translationValidation.open}
                onClose={() => setTranslationValidation({ open: false, missing: [], extra: [] })}
                closeOnDimmerClick={false}
                size='small'
            >
                <Header icon='warning sign' content={t.admin.translationValidationTitle} />
                <Modal.Content>
                    <p>{t.admin.translationValidationHint}</p>
                    {translationValidation.missing.length > 0 && (
                        <Message negative>
                            <Message.Header>{t.admin.translationMissingKeys} ({translationValidation.missing.length})</Message.Header>
                            <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 8 }}>
                                <ul>
                                    {translationValidation.missing.map((keyPath) => (
                                        <li key={`missing-${keyPath}`}><code>{keyPath}</code></li>
                                    ))}
                                </ul>
                            </div>
                        </Message>
                    )}
                    {translationValidation.extra.length > 0 && (
                        <Message warning>
                            <Message.Header>{t.admin.translationExtraKeys} ({translationValidation.extra.length})</Message.Header>
                            <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 8 }}>
                                <ul>
                                    {translationValidation.extra.map((keyPath) => (
                                        <li key={`extra-${keyPath}`}><code>{keyPath}</code></li>
                                    ))}
                                </ul>
                            </div>
                        </Message>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setTranslationValidation({ open: false, missing: [], extra: [] })}>
                        {t.admin.close}
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    )
}

export default AdminPanel
